import React from 'react';
import { WebsiteDesignProps } from './types';
import { Cpu, Zap, Gamepad2, Play } from 'lucide-react';
import { Button } from "@/components/ui/button";

const CyberAcademy: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Nexus E-Sports High";

    return (
        <div className="bg-[#050510] text-cyan-400 min-h-screen font-mono selection:bg-fuchsia-500 selection:text-white overflow-x-hidden">
             {/* Grid Background */}
             <div className="fixed inset-0 pointer-events-none opacity-20" style={{ 
                backgroundImage: 'linear-gradient(#22d3ee 1px, transparent 1px), linear-gradient(90deg, #22d3ee 1px, transparent 1px)', 
                backgroundSize: '40px 40px',
                transform: 'perspective(500px) rotateX(60deg) translateY(-100px) scale(2)'
            }}></div>

             <nav className="fixed w-full z-50 bg-[#050510]/80 backdrop-blur border-b border-cyan-900/50 px-6 py-4 flex justify-between items-center">
                 <div className="flex items-center gap-2">
                     <Zap className="text-fuchsia-500 fill-fuchsia-500 animate-pulse" />
                     <span className="text-xl font-bold italic tracking-tighter text-white">NEXUS<span className="text-cyan-400">.EDU</span></span>
                 </div>
                 <Button className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-none skew-x-[-10deg] border border-fuchsia-400 shadow-[0_0_15px_rgba(217,70,239,0.5)]">
                     <span className="skew-x-[10deg]">JACK_IN // LOGIN</span>
                 </Button>
             </nav>

             <main className="relative pt-32 pb-20 container mx-auto px-6">
                 <div className="flex flex-col lg:flex-row items-center gap-16 relative z-10">
                     <div className="lg:w-1/2">
                         <div className="inline-block px-2 py-1 bg-cyan-900/30 border border-cyan-500 text-xs text-cyan-400 mb-6">
                             SYSTEM STATUS: ONLINE
                         </div>
                         <h1 className="text-6xl md:text-8xl font-black italic text-white mb-6 leading-none shadow-cyan-500 drop-shadow-[0_0_10px_rgba(34,211,238,0.5)]">
                             LEVEL UP <br/> YOUR <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-fuchsia-500">FUTURE</span>
                         </h1>
                         <p className="text-lg text-slate-400 mb-10 border-l-4 border-fuchsia-500 pl-4">
                             The world's first accredited academy for E-Sports, Game Design, and Future Tech. Don't just play the game. Build it.
                         </p>
                         <div className="flex gap-4">
                             <Button className="h-14 px-8 bg-cyan-500 hover:bg-cyan-400 text-black font-bold text-lg rounded-none clip-path-polygon">
                                 START GAME
                             </Button>
                             <Button variant="outline" className="h-14 px-8 border-cyan-500 text-cyan-400 hover:bg-cyan-900/20 text-lg rounded-none">
                                 VIEW STATS
                             </Button>
                         </div>
                     </div>
                     
                     <div className="lg:w-1/2 relative">
                         <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-fuchsia-500 rounded-full blur-[100px] opacity-20"></div>
                         <img src="https://images.unsplash.com/photo-1542751371-adc38448a05e?q=80&w=2070&auto=format&fit=crop" className="relative w-full border-2 border-cyan-500/50 shadow-[0_0_30px_rgba(34,211,238,0.2)] rounded-lg" alt="Gaming Setup" />
                         
                         {/* Floating Cards */}
                         <div className="absolute -bottom-6 -left-6 bg-[#050510] border border-fuchsia-500 p-4 shadow-[0_0_20px_rgba(217,70,239,0.3)] animate-bounce delay-700">
                             <div className="text-xs text-fuchsia-500 mb-1">CURRENT CLASS</div>
                             <div className="text-white font-bold text-xl">Game Theory 101</div>
                         </div>
                     </div>
                 </div>

                 <div className="mt-32 grid grid-cols-1 md:grid-cols-3 gap-6">
                     {[
                         { icon: Cpu, title: "Hardware Eng", desc: "Build the rigs that power the metaverse." },
                         { icon: Gamepad2, title: "Pro Gaming", desc: "Train with champions. Master the meta." },
                         { icon: Play, title: "Streaming", desc: "Content creation and digital broadcasting." },
                     ].map((item, i) => (
                         <div key={i} className="bg-[#0a0a1a] p-8 border border-cyan-900 hover:border-cyan-400 transition-colors group">
                             <item.icon className="w-10 h-10 text-cyan-600 group-hover:text-cyan-400 mb-4 transition-colors" />
                             <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                             <p className="text-slate-500 text-sm">{item.desc}</p>
                         </div>
                     ))}
                 </div>
             </main>
        </div>
    );
};

export default CyberAcademy;
